import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, Mail, Save, Upload } from "lucide-react";
import { api, API_BASE } from "@/lib/api";
import { useBranding } from "@/context/BrandingContext";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { getErrorMessage } from "@/lib/errors";

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

export default function OrgSettings() {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [testing, setTesting] = useState(false);
  const [logoFile, setLogoFile] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const logoInputRef = useRef(null);
  const { refresh: refreshBranding } = useBranding();

  useEffect(() => { api.get("/admin/org-settings").then(({ data }) => setForm(data)).catch((err) => toast.error(getErrorMessage(err))); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch("/admin/org-settings", {
        fiscal_year_start_month: form.fiscal_year_start_month,
        indent_to_po_tolerance_pct: form.indent_to_po_tolerance_pct,
        po_to_grn_tolerance_pct: form.po_to_grn_tolerance_pct,
        bill_rate_tolerance_pct: form.bill_rate_tolerance_pct,
        allow_negative_stock: form.allow_negative_stock,
        decimal_places: form.decimal_places,
        display_name: form.display_name || null,
        tagline: form.tagline || null,
      });
      setForm(data);
      refreshBranding();
      toast.success("Settings saved");
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const uploadLogo = async () => {
    if (!logoFile) return;
    setUploadingLogo(true);
    try {
      const fd = new FormData();
      fd.append("file", logoFile);
      const { data } = await api.post("/admin/org-settings/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm(data);
      setLogoFile(null);
      if (logoInputRef.current) logoInputRef.current.value = "";
      await refreshBranding();
      toast.success("Logo updated — it shows on the login screen and sidebar");
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setUploadingLogo(false);
    }
  };

  const sendTest = async () => {
    if (!testEmail) return;
    setTesting(true);
    try {
      const { data } = await api.post("/admin/org-settings/smtp-test", { to_email: testEmail });
      toast.success(data.detail);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setTesting(false);
    }
  };

  if (!form) return <p className="text-sm text-muted-foreground">Loading…</p>;

  return (
    <div className="space-y-6" data-testid="org-settings-page">
      <PageHeader title="Organization Settings" description="Branding, fiscal year, matching tolerances, stock policy and SMTP." />
      <div className="max-w-xl space-y-5 rounded-xl border border-border bg-card p-6">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>App display name</Label>
            <Input placeholder="EPC DPR Platform" value={form.display_name || ""} onChange={(e) => setForm({ ...form, display_name: e.target.value })} className="mt-1.5" data-testid="org-display-name-input" />
          </div>
          <div>
            <Label>Tagline</Label>
            <Input placeholder="Plan · Build · Deliver" value={form.tagline || ""} onChange={(e) => setForm({ ...form, tagline: e.target.value })} className="mt-1.5" data-testid="org-tagline-input" />
          </div>
        </div>
        <div className="rounded-lg border border-border p-3">
          <Label>Logo</Label>
          <div className="mt-2 flex items-center gap-4">
            <img
              src={form.has_logo ? `${API_BASE}/public/branding/logo` : "/brand/bnb-logo.png"}
              alt="Current logo"
              className="h-12 w-20 shrink-0 rounded-md border border-border bg-white object-contain p-1"
              data-testid="org-logo-preview"
            />
            <div className="flex flex-1 items-center gap-2">
              <input
                ref={logoInputRef}
                type="file"
                accept=".png,.jpg,.jpeg"
                className="hidden"
                data-testid="org-logo-file-input"
                onChange={(e) => setLogoFile(e.target.files?.[0] || null)}
              />
              <Button type="button" variant="outline" onClick={() => logoInputRef.current?.click()} data-testid="org-logo-choose-button">
                Choose file
              </Button>
              {logoFile && <span className="min-w-0 truncate text-xs text-muted-foreground">{logoFile.name}</span>}
              <Button type="button" onClick={uploadLogo} disabled={!logoFile || uploadingLogo} data-testid="org-logo-upload-button">
                {uploadingLogo ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Upload
              </Button>
            </div>
          </div>
          <p className="mt-2 text-[11px] text-muted-foreground">PNG or JPG up to 5MB. Used on the login screen, sidebar and reports.</p>
        </div>
        <div>
          <Label>Fiscal year starts in</Label>
          <Select value={String(form.fiscal_year_start_month)} onValueChange={(v) => setForm({ ...form, fiscal_year_start_month: Number(v) })}>
            <SelectTrigger className="mt-1.5" data-testid="fiscal-year-start-select"><SelectValue /></SelectTrigger>
            <SelectContent>{MONTHS.map((m, i) => <SelectItem key={m} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div><Label>Indent → PO tolerance %</Label><Input type="number" step="0.5" value={form.indent_to_po_tolerance_pct} onChange={(e) => setForm({ ...form, indent_to_po_tolerance_pct: e.target.value })} className="mt-1.5" data-testid="indent-to-po-tolerance-input" /></div>
          <div><Label>PO → GRN tolerance %</Label><Input type="number" step="0.5" value={form.po_to_grn_tolerance_pct} onChange={(e) => setForm({ ...form, po_to_grn_tolerance_pct: e.target.value })} className="mt-1.5" data-testid="po-to-grn-tolerance-input" /></div>
          <div><Label>Bill rate tolerance %</Label><Input type="number" step="0.5" value={form.bill_rate_tolerance_pct} onChange={(e) => setForm({ ...form, bill_rate_tolerance_pct: e.target.value })} className="mt-1.5" data-testid="bill-rate-tolerance-input" /></div>
        </div>
        <div className="flex items-center justify-between rounded-lg border border-border p-3">
          <div>
            <p className="text-sm text-foreground">Allow negative stock</p>
            <p className="text-xs text-muted-foreground">Lets goods issues push a warehouse balance below zero.</p>
          </div>
          <Switch checked={form.allow_negative_stock} onCheckedChange={(v) => setForm({ ...form, allow_negative_stock: v })} data-testid="allow-negative-stock-switch" />
        </div>
        <div className="w-40"><Label>Decimal places</Label><Input type="number" min="0" max="4" value={form.decimal_places} onChange={(e) => setForm({ ...form, decimal_places: e.target.value })} className="mt-1.5" data-testid="decimal-places-input" /></div>
        <Button onClick={save} disabled={saving} data-testid="save-org-settings-button">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save Settings</Button>

        <div className="border-t border-border pt-4">
          <Label>SMTP test</Label>
          <div className="mt-1.5 flex gap-2">
            <Input type="email" placeholder="you@company.com" value={testEmail} onChange={(e) => setTestEmail(e.target.value)} data-testid="smtp-test-email-input" />
            <Button variant="outline" onClick={sendTest} disabled={testing || !testEmail} data-testid="smtp-test-send-button">{testing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />} Send test</Button>
          </div>
        </div>
      </div>
    </div>
  );
}
